import React, { forwardRef, useId } from "react";
import { FormikProps, getIn } from "formik";
import InputText from "./input-text.component";

interface TextFieldProps<Values extends object> {
	name: string;
	label: string;
	formik: FormikProps<Values>;
	type?: string;
	placeholder?: string;
	disabled?: boolean;
	className?: string;
	fallbackValue?: string;
	icon?: React.ReactNode;
	securetext?: boolean;
	hasClearField?: boolean;
	onClearField?: () => void;
}

function TextFieldInner<Values extends object>(
	{
		name,
		label,
		formik,
		type = "text",
		placeholder,
		disabled,
		className,
		fallbackValue = "",
		icon,
		securetext = false,
		hasClearField = false,
		onClearField,
	}: TextFieldProps<Values>,
	ref: React.ForwardedRef<HTMLInputElement>
) {
	const generatedId = useId();
	const value = getIn(formik.values, name);
	const error = getIn(formik.errors, name);
	const touched = getIn(formik.touched, name);
	const hasError = (formik.submitCount > 0 || !!touched) && typeof error === "string";

	return (
		<InputText
			ref={ref}
			id={generatedId}
			name={name}
			label={label}
			type={type}
			placeholder={placeholder}
			disabled={disabled}
			inputClassName={className}
			value={String(value ?? fallbackValue)}
			onChange={formik.handleChange}
			onBlur={formik.handleBlur}
			onWheel={type === "number" ? (e) => e.currentTarget.blur() : undefined}
			error={hasError ? error : undefined}
			icon={icon}
			securetext={securetext}
			hasClearField={hasClearField}
			onClearField={onClearField}
		/>
	);
}

const TextField = forwardRef(TextFieldInner) as <Values extends object>(
	props: TextFieldProps<Values> & { ref?: React.ForwardedRef<HTMLInputElement> }
) => JSX.Element;

export default TextField;
